/**
 * Chart spec — the small, data-only shape used by both chart paths:
 *   1. LLM-emitted: the assistant writes a ```chart fenced JSON block.
 *   2. Deterministic: built client-side from a run_sql tool_result's rows
 *      (the LLM never touches the numbers — no hallucination risk).
 *
 * Everything here is pure + data-only. We NEVER eval the spec or accept
 * function/JS fields — only the whitelisted keys below survive validation,
 * so an LLM-emitted spec can't carry executable content into Chart.js.
 */

export type ChartType = "bar" | "line" | "pie" | "doughnut" | "radar";
const CHART_TYPES: readonly ChartType[] = ["bar", "line", "pie", "doughnut", "radar"];

export interface ChartDataset {
  label?: string;
  data: number[];
}

export interface ChartSpec {
  type: ChartType;
  title?: string;
  labels: string[];
  /** Real name of the label/category column (e.g. "product"), so the data
   *  table can show it instead of a generic "Category" header. */
  labelTitle?: string;
  datasets: ChartDataset[];
  stacked?: boolean;
}

/** Key/ID columns are identifiers, not measures — never chart them as a series
 *  (e.g. `contract_id` plotted as a quantity). */
const ID_COL = /(^id$|_id$|_uuid$|_sha\d*$)/i;

const MAX_LABELS = 50;
const MAX_DATASETS = 6;

function toNum(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    // tolerate "1.234,56" (da) and "1,234.56" (en) lightly: strip spaces.
    const n = Number(v.replace(/\s/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Validate + normalise an untrusted (LLM- or user-supplied) chart spec.
 * Returns a clean ChartSpec or null if it isn't a usable chart. Strips all
 * non-whitelisted keys; never throws.
 */
export function validateChartSpec(raw: unknown): ChartSpec | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const type = o.type;
  if (typeof type !== "string" || !CHART_TYPES.includes(type as ChartType)) {
    return null;
  }

  const labelsRaw = Array.isArray(o.labels) ? o.labels : [];
  const labels = labelsRaw
    .slice(0, MAX_LABELS)
    .map((l) => (l == null ? "" : String(l)));
  if (labels.length === 0) return null;

  const dsRaw = Array.isArray(o.datasets) ? o.datasets : [];
  const datasets: ChartDataset[] = [];
  for (const d of dsRaw.slice(0, MAX_DATASETS)) {
    if (!d || typeof d !== "object") continue;
    const dd = d as Record<string, unknown>;
    const dataArr = Array.isArray(dd.data) ? dd.data : [];
    const data = dataArr.slice(0, labels.length).map((v) => toNum(v) ?? 0);
    // pad to label count so chart.js lines up
    while (data.length < labels.length) data.push(0);
    if (data.some((n) => n !== 0) || dataArr.length > 0) {
      datasets.push({
        label: typeof dd.label === "string" ? dd.label : undefined,
        data,
      });
    }
  }
  if (datasets.length === 0) return null;

  return {
    type: type as ChartType,
    title: typeof o.title === "string" ? o.title.slice(0, 200) : undefined,
    labels,
    datasets,
    stacked: o.stacked === true,
  };
}

/**
 * A run_sql chart *directive*. The model decides WHETHER and HOW to chart by
 * emitting a ```chart fence carrying `{"source":"run_sql", ...}` and NO data;
 * the frontend then fills the numbers from the real run_sql tool result (via
 * buildSpecFromRows). This keeps charting intent-driven (the LLM opts in only
 * when a visualization actually helps) while keeping the numbers deterministic
 * (the model never hand-types — and so can't hallucinate — bar heights).
 */
export interface ChartDirective {
  source: "run_sql";
  type?: ChartType;
  title?: string;
  maxRows?: number;
}

/** Parse a ```chart fence body into a run_sql directive, or null if it isn't
 *  one (a directive is `source:"run_sql"` with no inline datasets). Never
 *  throws; unknown keys are dropped. */
export function parseChartDirective(raw: unknown): ChartDirective | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.source !== "run_sql") return null;
  const type =
    typeof o.type === "string" && CHART_TYPES.includes(o.type as ChartType)
      ? (o.type as ChartType)
      : undefined;
  const maxRows =
    typeof o.maxRows === "number" && Number.isFinite(o.maxRows)
      ? o.maxRows
      : undefined;
  return {
    source: "run_sql",
    type,
    title: typeof o.title === "string" ? o.title.slice(0, 200) : undefined,
    maxRows,
  };
}

/**
 * Deterministically build a chart spec from tabular rows (a run_sql
 * tool_result). The LLM is NOT involved in the numbers — they're the real
 * query output. `opts.type`/`opts.title` let a directive override the
 * auto-picked chart type / supply a heading. Returns null when the rows aren't
 * chartable (sense-platform's heuristic: needs ≥2 rows, one categorical key +
 * ≥1 numeric column).
 */
export function buildSpecFromRows(
  rows: Array<Record<string, unknown>>,
  opts: { maxRows?: number; type?: ChartType; title?: string } = {},
): ChartSpec | null {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const maxRows = opts.maxRows ?? 40;
  const sample = rows[0];
  if (!sample || typeof sample !== "object") return null;
  const cols = Object.keys(sample);

  // numeric columns = numeric in the first row, excluding ID/key columns
  // (an identifier like contract_id is not a measure and must not be plotted).
  const numericCols = cols.filter((c) => !ID_COL.test(c) && toNum(sample[c]) !== null);
  // label column = first non-numeric, non-ID column, else first non-numeric, else col 0
  const labelCol =
    cols.find((c) => !numericCols.includes(c) && !ID_COL.test(c)) ??
    cols.find((c) => !numericCols.includes(c)) ??
    cols[0];
  const valueCols = numericCols.filter((c) => c !== labelCol).slice(0, 3);
  if (valueCols.length === 0) return null;

  const used = rows.slice(0, maxRows);
  const labels = used.map((r) => String(r[labelCol] ?? ""));
  const datasets: ChartDataset[] = valueCols.map((c) => ({
    label: c,
    data: used.map((r) => toNum(r[c]) ?? 0),
  }));

  // A single numeric column over a small categorical set → bar; many rows →
  // line reads better as a trend. Keep it simple + predictable. A directive
  // may override the auto-pick.
  const type: ChartType =
    opts.type ?? (used.length > 12 && valueCols.length === 1 ? "line" : "bar");
  return { type, title: opts.title, labels, labelTitle: labelCol, datasets };
}

/** The brand-ish palette (matches sense-platform's + Choir accent). */
export const CHART_PALETTE = [
  "#279DB6", "#e74c3c", "#3498db", "#ca8a04", "#16a34a", "#9333ea",
];

/**
 * Map a validated ChartSpec → a Chart.js config object. `ink`/`grid` are the
 * resolved theme colours (so dark mode works) read from CSS vars by the
 * caller. Pure — no Chart.js import here (keeps this tree-shakeable + unit
 * testable).
 */
export function toChartJsConfig(
  spec: ChartSpec,
  theme: { ink: string; grid: string },
): Record<string, unknown> {
  const isPieLike = spec.type === "pie" || spec.type === "doughnut";
  const datasets = spec.datasets.map((ds, i) => {
    const color = CHART_PALETTE[i % CHART_PALETTE.length];
    if (isPieLike) {
      return {
        label: ds.label,
        data: ds.data,
        backgroundColor: ds.data.map((_, j) => CHART_PALETTE[j % CHART_PALETTE.length]),
        borderWidth: 1,
      };
    }
    return {
      label: ds.label,
      data: ds.data,
      backgroundColor: spec.type === "line" ? "transparent" : color,
      borderColor: color,
      borderWidth: 2,
      tension: spec.type === "line" ? 0.25 : 0,
      pointRadius: spec.type === "line" ? 2 : 0,
      fill: false,
    };
  });

  const showLegend = isPieLike || spec.datasets.length > 1 || spec.type === "radar";
  const scales = isPieLike || spec.type === "radar"
    ? undefined
    : {
        x: {
          stacked: spec.stacked === true,
          ticks: { color: spec.type, autoSkip: true, maxRotation: 0 },
          grid: { color: theme.grid },
        },
        y: {
          stacked: spec.stacked === true,
          beginAtZero: true,
          ticks: { color: theme.ink },
          grid: { color: theme.grid },
        },
      };

  return {
    type: spec.type,
    data: { labels: spec.labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      plugins: {
        legend: { display: showLegend, labels: { color: theme.ink } },
        title: spec.title
          ? { display: true, text: spec.title, color: theme.ink }
          : { display: false },
        tooltip: { enabled: true },
      },
      ...(scales ? { scales } : {}),
    },
  };
}
